import {
  addDays,
  type BulkSet,
  dayLabel,
  formatClock,
  formatEntryDuration,
  formatTimeOfDay,
  groupByDay,
  groupSimilar,
  localDate,
  localMinutes,
  MAX_DAY_SEC,
  parseDuration,
  parseTimeOfDay,
  type TimeEntry,
  type TimeEntryPatch,
  weekRange,
  zonedTimeToUtc,
} from '@taim/shared';
import { EllipsisVertical, ListChecks, Play, Square } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useProjects, useTagScope } from '@/features/catalog/hooks';
import type { ProjectPickerValue } from '@/features/catalog/ProjectPicker';
import { useStableCallback } from '@/hooks/useStableCallback';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { BulkEditDialog } from './BulkEditDialog';
import {
  useBulkEntries,
  useCalendar,
  useDeleteEntry,
  useDuplicateEntry,
  useNow,
  useRunningEntry,
  useStartTimer,
  useStopTimer,
  useToday,
  useTrackerEntries,
  useUpdateEntry,
} from './hooks';
import { BillableToggle, CommitInput, DatePicker, errorMessage } from './inputs';
import { ProjectPicker, TagPicker } from './pickers';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthDay = (date: string) => `${MONTHS[Number(date.slice(5, 7)) - 1]} ${Number(date.slice(8, 10))}`;

const entryCount = (n: number) => (n === 1 ? '1 entry' : `${n} entries`);

/*
 * Success toasts follow the server's answer (`mutateAsync`): per-call `mutate` callbacks only fire
 * for the latest call and not at all once the row has gone (a delete hides it at once). Repeated
 * edits of one entry share a toast id, so the latest replaces the toast instead of stacking.
 */

/** Surfaces mutation errors of the list in one place; `revision` resets inputs after a failure. */
type Report = ((e: unknown) => void) & { revision: number };

/** The client whose tags a project's entries use: null without a project, undefined while loading. */
type ClientOf = (projectId: string | null) => string | null | undefined;

/**
 * What rows do, shared by the whole list (one set of mutation observers instead of one per row).
 * Stable for the list's lifetime, so memoized rows don't re-render because of it.
 */
type EntryActions = {
  patch: (entry: TimeEntry, patch: TimeEntryPatch) => void;
  /** A project change, dropping tags of another client in the same patch. */
  changeProject: (entry: TimeEntry, value: ProjectPickerValue | null) => void;
  /** Sets details of a group of similar entries. */
  setGroup: (entries: TimeEntry[], set: BulkSet) => void;
  groupProject: (entries: TimeEntry[], value: ProjectPickerValue | null) => void;
  duplicate: (entry: TimeEntry) => void;
  remove: (entry: TimeEntry) => void;
  continueEntry: (entry: TimeEntry) => void;
};

/** Props shared by every row of the list (all stable unless they really change). */
type RowContext = { report: Report; actions: EntryActions; allowNone: boolean };

/**
 * memo() comparison for rows and days: props equal by identity, except arrays of entries, which
 * are equal when their items are (entries keep their identity until they change).
 */
function sameProps<P extends object>(prev: P, next: P): boolean {
  const keys = Object.keys(next) as Array<keyof P>;
  return (
    keys.length === Object.keys(prev).length &&
    keys.every((k) => {
      const a = prev[k];
      const b = next[k];
      return (
        Object.is(a, b) ||
        (Array.isArray(a) &&
          Array.isArray(b) &&
          a.length === b.length &&
          a.every((item, i) => Object.is(item, b[i])))
      );
    })
  );
}

function useEntryActions(report: Report): EntryActions {
  const update = useUpdateEntry();
  const remove = useDeleteEntry();
  const duplicate = useDuplicateEntry();
  const bulk = useBulkEntries();
  const start = useStartTimer();
  const tagScope = useTagScope();

  const patch = useStableCallback((entry: TimeEntry, p: TimeEntryPatch) =>
    update
      .mutateAsync({ id: entry.id, ...p })
      .then(() => toast.success('Time entry updated', { id: `entry-update-${entry.id}` }), report),
  );
  const setGroup = useStableCallback((entries: TimeEntry[], set: BulkSet) =>
    bulk
      .mutateAsync({ action: 'update', ids: entries.map((e) => e.id), set })
      .then(
        () =>
          toast.success(`${entryCount(entries.length)} updated`, { id: `entries-update-${entries[0]!.id}` }),
        report,
      ),
  );
  const changeProject = useStableCallback((entry: TimeEntry, value: ProjectPickerValue | null) =>
    patch(entry, tagScope.projectPatch(value, entry.tagIds)),
  );
  const groupProject = useStableCallback((entries: TimeEntry[], value: ProjectPickerValue | null) =>
    setGroup(entries, tagScope.projectPatch(value, entries[0]!.tagIds)),
  );
  const duplicateEntry = useStableCallback((entry: TimeEntry) =>
    duplicate.mutateAsync(entry.id).then(() => toast.success('Time entry duplicated'), report),
  );
  const removeEntry = useStableCallback((entry: TimeEntry) =>
    remove.mutateAsync(entry.id).then(() => toast.success('Time entry deleted'), report),
  );
  const continueEntry = useStableCallback((e: TimeEntry) =>
    start.mutate(
      {
        description: e.description,
        projectId: e.projectId,
        taskId: e.taskId,
        tagIds: e.tagIds,
        billable: e.billable,
      },
      { onError: report },
    ),
  );
  return useMemo(
    () => ({
      patch,
      changeProject,
      setGroup,
      groupProject,
      duplicate: duplicateEntry,
      remove: removeEntry,
      continueEntry,
    }),
    [patch, changeProject, setGroup, groupProject, duplicateEntry, removeEntry, continueEntry],
  );
}

/** The tracker's entry list: weeks → days → (grouped) entries, with "Load more". */
export function EntryList() {
  const { timeZone, weekStart } = useCalendar();
  const { tenant } = useAuth();
  const today = useToday(timeZone);
  const thisWeek = weekRange(today, weekStart, timeZone).start;
  const query = useTrackerEntries();
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  // Changes only with `revision` (after a failed save), when every row resets its inputs.
  const report = useMemo<Report>(
    () =>
      Object.assign(
        (e: unknown) => {
          setError(errorMessage(e));
          setRevision((r) => r + 1);
        },
        { revision },
      ),
    [revision],
  );
  const actions = useEntryActions(report);
  const allowNone = !tenant.requireProject;
  const row = useMemo<RowContext>(() => ({ report, actions, allowNone }), [report, actions, allowNone]);

  // Looked up once here (rows get the plain client id) instead of a tag scope per row.
  const projects = useProjects({ archived: 'all' }).data;
  const clientOf = useMemo<ClientOf>(() => {
    const clients = new Map(projects?.map((p) => [p.id, p.clientId]));
    return (projectId) => {
      if (!projectId) return null;
      return clients.has(projectId) ? (clients.get(projectId) ?? null) : undefined;
    };
  }, [projects]);

  const running = useRunningEntry().data ?? null;
  // The running timer shows live in its day, unless it has run for over a day (a forgotten
  // timer): that one only shows in the top bar, where it can be stopped.
  const live = running && Date.now() - Date.parse(running.startAt) <= MAX_DAY_SEC * 1000 ? running : null;
  const liveWeek = live ? weekRange(localDate(live.startAt, timeZone), weekStart, timeZone).start : null;

  const pages = query.data?.pages ?? [];
  const weeks = pages
    .filter((p) => p.weekStart)
    .map((p) => ({
      weekStart: p.weekStart!,
      entries: [
        ...p.entries.filter((e) => e.endAt !== null),
        ...(live && liveWeek === p.weekStart ? [live] : []),
      ],
    }))
    .filter((w) => w.entries.length > 0);

  const weekTitle = (start: string) =>
    start === thisWeek
      ? 'This week'
      : start === addDays(thisWeek, -7)
        ? 'Last week'
        : `${monthDay(start)} - ${monthDay(addDays(start, 6))}`;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
      {error && (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
      {query.isSuccess && weeks.length === 0 && !query.hasNextPage && (
        <p className="py-10 text-center text-muted-foreground">
          Let’s start tracking! Your entries will show up here.
        </p>
      )}
      {weeks.map((week) => {
        const total = week.entries.reduce((s, e) => s + (e.durationSec ?? 0), 0);
        return (
          <div key={week.weekStart} className="grid grid-cols-[minmax(0,1fr)] gap-3">
            <div className="flex items-center justify-between px-1 text-muted-foreground text-sm">
              <span>{weekTitle(week.weekStart)}</span>
              <span>
                Week total:{' '}
                <output aria-label="Week total" className="font-medium text-foreground">
                  {formatEntryDuration(total)}
                </output>
              </span>
            </div>
            {groupByDay(week.entries, timeZone).map((d) => (
              <DayCard
                key={d.date}
                entries={d.entries}
                totalSec={d.totalSec}
                label={dayLabel(d.date, today)}
                clientOf={clientOf}
                row={row}
              />
            ))}
          </div>
        );
      })}
      {query.hasNextPage && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}

type DayCardProps = {
  entries: TimeEntry[];
  totalSec: number;
  label: string;
  clientOf: ClientOf;
  row: RowContext;
};

/** One day of the list. Re-renders only when its entries (or the list's shared props) change. */
const DayCard = memo(function DayCard({ entries, totalSec, label, clientOf, row }: DayCardProps) {
  const { report } = row;
  const [bulk, setBulk] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const bulkMutation = useBulkEntries();
  const { weekStart } = useCalendar();

  const ids = entries.map((e) => e.id);
  const chosen = ids.filter((id) => selected.has(id));
  const allChecked = chosen.length === ids.length && ids.length > 0;

  const toggle = useCallback(
    (id: string) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    [],
  );

  const deleteSelected = () =>
    bulkMutation.mutateAsync({ action: 'delete', ids: chosen }).then(() => {
      setSelected(new Set());
      toast.success(`${entryCount(chosen.length)} deleted`);
    }, report);

  return (
    <section aria-label={label} className="rounded-sm border bg-card shadow-xs">
      <header className="flex items-center gap-3 border-b bg-muted/60 px-4 py-2 text-sm">
        {bulk && (
          <Checkbox
            aria-label="Select all"
            checked={allChecked}
            onCheckedChange={() => setSelected(allChecked ? new Set() : new Set(ids))}
          />
        )}
        <span className="font-medium text-muted-foreground">{label}</span>
        {bulk && (
          <>
            <Button
              size="xs"
              variant="outline"
              disabled={chosen.length === 0}
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
            <Button
              size="xs"
              variant="outline"
              className="text-destructive"
              disabled={chosen.length === 0}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
          </>
        )}
        <span className="ml-auto text-muted-foreground">
          Total:{' '}
          <output aria-label="Day total" className="font-medium text-foreground">
            {formatEntryDuration(totalSec)}
          </output>
        </span>
        <Button
          size="icon-xs"
          variant="ghost"
          aria-label="Bulk edit"
          aria-pressed={bulk}
          title="Bulk edit"
          onClick={() => {
            setBulk((b) => !b);
            setSelected(new Set());
          }}
          className={cn(bulk ? 'text-primary' : 'text-muted-foreground')}
        >
          <ListChecks />
        </Button>
      </header>
      <ul className="divide-y">
        {bulk
          ? entries.map((e) => (
              <EntryRow
                key={e.id}
                entry={e}
                clientId={clientOf(e.projectId)}
                row={row}
                selected={selected.has(e.id)}
                onToggle={toggle}
              />
            ))
          : // Keyed by entry, not by the details that group them: an edit (e.g. ticking a tag)
            // then updates the row in place, so its open picker stays open.
            groupSimilar(entries).map((g) =>
              g.entries.length === 1 ? (
                <EntryRow
                  key={g.entries[0]!.id}
                  entry={g.entries[0]!}
                  clientId={clientOf(g.entries[0]!.projectId)}
                  row={row}
                />
              ) : (
                <GroupRow
                  key={`group-${g.entries[0]!.id}`}
                  entries={g.entries}
                  totalSec={g.totalSec}
                  clientId={clientOf(g.entries[0]!.projectId)}
                  row={row}
                />
              ),
            )}
      </ul>
      {editing && (
        <BulkEditDialog
          count={chosen.length}
          weekStart={weekStart}
          pending={bulkMutation.isPending}
          error={bulkMutation.error}
          onClose={() => setEditing(false)}
          onSubmit={(set) =>
            bulkMutation.mutateAsync({ action: 'update', ids: chosen, set }).then(
              () => {
                setEditing(false);
                setSelected(new Set());
                toast.success(`${entryCount(chosen.length)} updated`);
              },
              // The dialog shows the error.
              () => {},
            )
          }
        />
      )}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {chosen.length} {chosen.length === 1 ? 'entry' : 'entries'}?
            </AlertDialogTitle>
            <AlertDialogDescription>This can’t be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={deleteSelected}>
              DELETE
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}, sameProps);

const pickerValue = (e: Pick<TimeEntry, 'projectId' | 'taskId'>): ProjectPickerValue | null =>
  e.projectId ? { projectId: e.projectId, taskId: e.taskId } : null;

/** The project picker's value for an entry, kept while its project and task stay the same. */
const usePickerValue = (e: Pick<TimeEntry, 'projectId' | 'taskId'>) => {
  const { projectId, taskId } = e;
  return useMemo(() => pickerValue({ projectId, taskId }), [projectId, taskId]);
};

/** Stops the running timer from its row in the list. */
function StopButton({ report }: { report: Report }) {
  const stop = useStopTimer();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label="Stop timer"
      title="Stop timer"
      disabled={stop.isPending}
      onClick={() => stop.mutate({}, { onError: report })}
      className="text-destructive"
    >
      <Square className="fill-current" />
    </Button>
  );
}

/** The ticking duration of a running entry (plus `baseSec` of finished ones in its group). */
function RunningDuration({
  startAt,
  baseSec = 0,
  className,
}: {
  startAt: string;
  baseSec?: number;
  className?: string;
}) {
  const now = useNow(true);
  const sec = baseSec + Math.max(0, Math.floor((now - Date.parse(startAt)) / 1000));
  return (
    <output aria-label="Running duration" className={cn('font-medium text-primary tabular-nums', className)}>
      {formatClock(sec)}
    </output>
  );
}

function ContinueButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label="Continue"
      title="Continue timer for this activity"
      onClick={onClick}
      className="text-muted-foreground"
    >
      <Play />
    </Button>
  );
}

type EntryRowProps = {
  entry: TimeEntry;
  /** The entry's project's client (whose tags it may carry). */
  clientId: string | null | undefined;
  row: RowContext;
  /** Bulk edit: whether the row is ticked, and how to tick it. */
  selected?: boolean;
  onToggle?: (id: string) => void;
};

/**
 * One time entry with Clockify's inline editing. Memoized: an edit of another entry (or anything
 * else in the list) doesn't re-render it.
 */
const EntryRow = memo(function EntryRow({ entry, clientId, row, selected, onToggle }: EntryRowProps) {
  const { report, actions, allowNone } = row;
  const { timeZone, weekStart } = useCalendar();

  const patch = (p: TimeEntryPatch) => actions.patch(entry, p);
  const project = usePickerValue(entry);
  const onProject = useStableCallback((v: ProjectPickerValue | null) => actions.changeProject(entry, v));
  const onTags = useStableCallback((tagIds: string[]) => actions.patch(entry, { tagIds }));

  const isRunning = entry.endAt === null;
  const startMs = Date.parse(entry.startAt);
  // A running entry ends now: read when an edit is made (the row doesn't re-render as time passes).
  const endMsNow = () => (entry.endAt ? Date.parse(entry.endAt) : Date.now());
  const date = localDate(entry.startAt, timeZone);

  const commitStart = (text: string) => {
    const m = parseTimeOfDay(text);
    if (m === null) return false;
    const startAt = zonedTimeToUtc(date, m, timeZone);
    if (startAt.getTime() > endMsNow()) return false;
    patch({ startAt: startAt.toISOString() });
    return formatTimeOfDay(m);
  };
  const commitEnd = (text: string) => {
    const m = parseTimeOfDay(text);
    if (m === null) return false;
    let endAt = zonedTimeToUtc(date, m, timeZone);
    // An end before the start means the entry runs past midnight.
    if (endAt.getTime() < startMs) endAt = zonedTimeToUtc(addDays(date, 1), m, timeZone);
    patch({ endAt: endAt.toISOString() });
    return formatTimeOfDay(m);
  };
  const commitDuration = (text: string) => {
    const sec = parseDuration(text);
    if (sec === null) return false;
    patch({ endAt: new Date(startMs + sec * 1000).toISOString() });
    return formatEntryDuration(sec);
  };
  const changeDate = (next: string) => {
    if (next === date) return;
    const startAt = zonedTimeToUtc(next, localMinutes(entry.startAt, timeZone), timeZone);
    patch({
      startAt: startAt.toISOString(),
      endAt: new Date(startAt.getTime() + (endMsNow() - startMs)).toISOString(),
    });
  };

  return (
    <li
      className={cn(
        'flex items-center gap-1 px-4 py-1.5 has-[[aria-label=Description]:focus]:flex-wrap',
        isRunning && 'bg-primary/5',
      )}
    >
      {onToggle && (
        <Checkbox
          aria-label={`Select ${entry.description || 'entry without description'}`}
          checked={selected}
          onCheckedChange={() => onToggle(entry.id)}
          className="mr-2"
        />
      )}
      <CommitInput
        aria-label="Description"
        placeholder="Add description"
        display={entry.description}
        revision={report.revision}
        onCommit={(text) => {
          patch({ description: text.trim() });
          return text.trim();
        }}
        fullTextOnHover
        className="h-8 w-60 min-w-24 truncate border-transparent shadow-none hover:border-input focus:basis-full"
      />
      <ProjectPicker
        value={project}
        onChange={onProject}
        allowNone={allowNone}
        className="w-60 min-w-16 shrink"
      />
      <TagPicker
        value={entry.tagIds}
        clientId={clientId}
        className="w-44 min-w-16 shrink"
        onChange={onTags}
      />
      <BillableToggle value={entry.billable} onChange={(billable) => patch({ billable })} />
      <div className="ml-auto flex shrink-0 items-center gap-0.5 border-l border-dashed pl-2">
        <CommitInput
          aria-label="Start time"
          display={formatTimeOfDay(localMinutes(entry.startAt, timeZone))}
          revision={report.revision}
          onCommit={commitStart}
          className="h-8 w-16 border-transparent px-1 text-center tabular-nums shadow-none hover:border-input"
        />
        <span className="text-muted-foreground">-</span>
        {isRunning ? (
          // A running entry has no end yet: it ends when the timer stops.
          <span className="w-16 px-1 text-center text-muted-foreground">now</span>
        ) : (
          <CommitInput
            aria-label="End time"
            display={formatTimeOfDay(localMinutes(endMsNow(), timeZone))}
            revision={report.revision}
            onCommit={commitEnd}
            className="h-8 w-16 border-transparent px-1 text-center tabular-nums shadow-none hover:border-input"
          />
        )}
        {isRunning ? (
          <span className="size-8 shrink-0" />
        ) : (
          <DatePicker value={date} onChange={changeDate} weekStart={weekStart} />
        )}
      </div>
      {isRunning ? (
        <RunningDuration
          startAt={entry.startAt}
          className="w-24 shrink-0 border-l border-dashed text-center"
        />
      ) : (
        <CommitInput
          aria-label="Duration"
          display={formatEntryDuration(entry.durationSec ?? 0)}
          revision={report.revision}
          onCommit={commitDuration}
          className="h-8 w-24 shrink-0 border-l border-transparent border-l-border border-dashed text-center font-medium tabular-nums shadow-none hover:border-input"
        />
      )}
      {isRunning ? (
        <StopButton report={report} />
      ) : (
        <ContinueButton onClick={() => actions.continueEntry(entry)} />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Actions"
            className="text-muted-foreground"
          >
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isRunning && (
            <DropdownMenuItem onSelect={() => actions.duplicate(entry)}>Duplicate</DropdownMenuItem>
          )}
          <DropdownMenuItem className="text-destructive" onSelect={() => actions.remove(entry)}>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}, sameProps);

type GroupRowProps = {
  entries: TimeEntry[];
  totalSec: number;
  /** The group's project's client (whose tags its entries may carry). */
  clientId: string | null | undefined;
  row: RowContext;
};

/** Similar entries of a day collapsed into one row (Clockify's grouped entries). Memoized like EntryRow. */
const GroupRow = memo(function GroupRow({ entries, totalSec, clientId, row }: GroupRowProps) {
  const { report, actions, allowNone } = row;
  const [open, setOpen] = useState(false);
  const first = entries[0]!;
  const running = entries.find((e) => e.endAt === null);
  const set = (s: BulkSet) => actions.setGroup(entries, s);
  const project = usePickerValue(first);
  const onProject = useStableCallback((v: ProjectPickerValue | null) => actions.groupProject(entries, v));
  const onTags = useStableCallback((tagIds: string[]) => actions.setGroup(entries, { tagIds }));

  return (
    <li className={cn(running && 'bg-primary/5')}>
      <div className="flex items-center gap-1 px-4 py-1.5 has-[[aria-label=Description]:focus]:flex-wrap">
        <button
          type="button"
          aria-label={`${open ? 'Hide' : 'Show'} ${entries.length} entries`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            'mr-1 flex size-7 shrink-0 items-center justify-center rounded border text-xs',
            open
              ? 'border-primary bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent',
          )}
        >
          {entries.length}
        </button>
        <CommitInput
          aria-label="Description"
          placeholder="Add description"
          display={first.description}
          revision={report.revision}
          onCommit={(text) => {
            set({ description: text.trim() });
            return text.trim();
          }}
          fullTextOnHover
          className="h-8 w-60 min-w-24 truncate border-transparent shadow-none hover:border-input focus:basis-full"
        />
        <ProjectPicker
          value={project}
          onChange={onProject}
          allowNone={allowNone}
          className="w-60 min-w-16 shrink"
        />
        <TagPicker
          value={first.tagIds}
          clientId={clientId}
          className="w-44 min-w-16 shrink"
          onChange={onTags}
        />
        <BillableToggle value={first.billable} onChange={(billable) => set({ billable })} />
        {running ? (
          <RunningDuration
            startAt={running.startAt}
            baseSec={totalSec}
            className="ml-auto w-24 shrink-0 border-l border-dashed pl-2 text-center text-sm"
          />
        ) : (
          <output
            aria-label="Duration"
            className="ml-auto w-24 shrink-0 border-l border-dashed pl-2 text-center font-medium text-sm tabular-nums"
          >
            {formatEntryDuration(totalSec)}
          </output>
        )}
        {running ? (
          <StopButton report={report} />
        ) : (
          <ContinueButton onClick={() => actions.continueEntry(first)} />
        )}
        <span className="size-8 shrink-0" />
      </div>
      {open && (
        <ul
          aria-label={`${first.description || 'Entries without description'} entries`}
          className="border-t bg-muted/30 pl-8"
        >
          {entries.map((e) => (
            <EntryRow key={e.id} entry={e} clientId={clientId} row={row} />
          ))}
        </ul>
      )}
    </li>
  );
}, sameProps);
